import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    ConnectApiError,
    connectApi,
    connectParityApi,
    connectPostsApi,
    type ChatMessage,
    type ChatScope,
    type MessageImportance,
    type PinScope
} from "./connectApi";
import { useAccessToken } from "@/auth/useAccessToken";

export function useTeams() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["teams", token],
        queryFn: () => connectApi.listTeams(token),
        enabled: Boolean(token)
    });
}

export function useCreateTeam() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (p: { slug: string; name: string; description?: string; visibility?: string }) =>
            connectApi.createTeam(p, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["teams"] })
    });
}

export function useChannels(teamId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["channels", teamId, token],
        queryFn: () => (teamId ? connectApi.listChannels(teamId, token) : Promise.resolve([])),
        enabled: Boolean(teamId) && Boolean(token)
    });
}

export function useCreateChannel(teamId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (p: { name: string; description?: string; kind?: string }) =>
            connectApi.createChannel(teamId, p, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["channels", teamId] })
    });
}

export function useMessages(channelId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["messages", channelId, token],
        queryFn: () => (channelId ? connectApi.listMessages(channelId, token) : Promise.resolve([])),
        enabled: Boolean(channelId) && Boolean(token)
    });
}

/**
 * Helpers to apply live hub events onto the TanStack Query cache for a
 * specific channel. Keeps the cache authoritative — no local duplicate
 * message state to drift.
 */
export function useMessagesCache(channelId: string | null) {
    const qc = useQueryClient();
    const token = useAccessToken();
    const key = ["messages", channelId, token] as const;

    return {
        appendMessage: (msg: ChatMessage) => {
            if (!channelId) return;
            qc.setQueryData<ChatMessage[]>([...key], (old) => {
                const list = old ?? [];
                if (list.some((m) => m.id === msg.id)) return list;
                return [...list, msg];
            });
        },
        replaceMessage: (msg: ChatMessage) => {
            if (!channelId) return;
            qc.setQueryData<ChatMessage[]>([...key], (old) =>
                (old ?? []).map((m) => (m.id === msg.id ? msg : m))
            );
        },
        removeMessage: (messageId: string) => {
            if (!channelId) return;
            qc.setQueryData<ChatMessage[]>([...key], (old) => (old ?? []).filter((m) => m.id !== messageId));
        },
        setPinned: (messageId: string, pinned: boolean) => {
            if (!channelId) return;
            qc.setQueryData<ChatMessage[]>([...key], (old) =>
                (old ?? []).map((m) => (m.id === messageId ? { ...m, isPinned: pinned } : m))
            );
        }
    };
}

export function useSendMessage(channelId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (input: string | {
            bodyHtml: string;
            importance?: MessageImportance;
            /** Channel posts: subject line. */
            subject?: string | null;
            /** Channel posts: announcement style (team owners). */
            isAnnouncement?: boolean;
        }) => {
            // Backwards-compatible: callers passing a plain string get the
            // historical Standard-importance flow; the object form lets the
            // composer set importance, a subject and the announcement style.
            if (typeof input === "string") return connectApi.sendMessage(channelId, input, token);
            return connectApi.sendMessage(channelId, input.bodyHtml, token, input.importance, {
                subject: input.subject,
                isAnnouncement: input.isAnnouncement
            });
        },
        // The hub push normally refreshes the posts; this covers a dropped connection.
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["channel-posts", channelId] })
    });
}

// ---------- Parity hooks ----------

export function useNotifications(unreadOnly = false) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["notifications", unreadOnly, token],
        queryFn: () => connectParityApi.listNotifications(unreadOnly, token),
        enabled: Boolean(token),
        refetchInterval: 15_000
    });
}

export function useMarkNotificationRead() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => connectParityApi.markNotificationRead(id, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["notifications"] })
    });
}

export function useMarkAllNotificationsRead() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: () => connectParityApi.markAllNotificationsRead(token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["notifications"] })
    });
}

export function useThreadReplies(channelId: string | null, messageId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["thread", channelId, messageId, token],
        queryFn: () =>
            channelId && messageId
                ? connectParityApi.listThreadReplies(channelId, messageId, token)
                : Promise.resolve([]),
        enabled: Boolean(channelId && messageId && token)
    });
}

export function useSendThreadReply(channelId: string, messageId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (bodyHtml: string) =>
            connectParityApi.sendThreadReply(channelId, messageId, bodyHtml, token),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["thread", channelId, messageId] });
            // The post's reply count and repliers.
            void qc.invalidateQueries({ queryKey: ["channel-posts", channelId] });
        }
    });
}

export function useConversations() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["conversations", token],
        queryFn: () => connectParityApi.listConversations(token),
        enabled: Boolean(token),
        refetchInterval: 30_000
    });
}

export function useCreateConversation() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (payload: {
            name?: string;
            participants: { userId: string; email: string; displayName?: string }[];
        }) => connectParityApi.createConversation(payload, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["conversations"] })
    });
}

export function useConversationMembers(id: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["conversation-members", id, token],
        queryFn: () =>
            id ? connectParityApi.listConversationMembers(id, token) : Promise.resolve([]),
        enabled: Boolean(id && token)
    });
}

/** Saved / bookmarked messages — Teams "Saved" surface. */
export function useSavedMessages() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["saved-messages", token],
        queryFn: () => connectParityApi.listSaved(token),
        enabled: Boolean(token)
    });
}

export function useSaveChannelMessage() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: ({ channelId, messageId }: { channelId: string; messageId: string }) =>
            connectParityApi.saveChannelMessage(channelId, messageId, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["saved-messages"] })
    });
}

export function useSaveDirectMessage() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: ({ chatId, messageId }: { chatId: string; messageId: string }) =>
            connectParityApi.saveDirectMessage(chatId, messageId, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["saved-messages"] })
    });
}

export function useSaveConversationMessage() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: ({ conversationId, messageId }: { conversationId: string; messageId: string }) =>
            connectParityApi.saveConversationMessage(conversationId, messageId, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["saved-messages"] })
    });
}

export function useUnsaveMessage() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (savedId: string) => connectParityApi.unsaveMessage(savedId, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["saved-messages"] })
    });
}

/** Tenant feature policy snapshot — drives proactive UI gating. */
export function useConnectPolicy() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["connect-policy", token],
        queryFn: () => connectParityApi.getPolicy(token),
        enabled: Boolean(token),
        // Mirrors the BFF's 60 s cache so the UI doesn't hammer the
        // endpoint on every navigation.
        staleTime: 60_000
    });
}

export function useGlobalSearch(query: string, take = 50) {
    const token = useAccessToken();
    const trimmed = query.trim();
    return useQuery({
        queryKey: ["search", trimmed, take, token],
        queryFn: () => connectParityApi.searchAll(trimmed, take, token),
        enabled: Boolean(token) && trimmed.length >= 2,
        // Search is user-typed and frequently re-issued — cache briefly so
        // toggling between filters doesn't refetch immediately.
        staleTime: 5_000
    });
}

/** `around`: show the messages either side of this one (a search hit or pin) instead of the latest. */
export function useConversationMessages(id: string | null, around: string | null = null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["conversation-messages", id, token, around],
        queryFn: () =>
            id ? connectParityApi.listConversationMessages(id, token, around) : Promise.resolve([]),
        enabled: Boolean(id && token),
        refetchInterval: 5_000
    });
}

export function useSendConversationMessage(id: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (bodyHtml: string) => connectParityApi.sendConversationMessage(id, bodyHtml, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["conversation-messages", id] })
    });
}

// --- 1:1 Direct Chats ---------------------------------------------------
// Same endpoint mail-web's chat dock uses (/api/dms). Lets the Chats
// view in Connect show conversations the user had in Mail, and vice
// versa — the storage is shared, so mail↔Connect round-trips work.

export function useDirectChats() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["direct-chats", token],
        queryFn: () => connectParityApi.listDirectChats(token),
        enabled: Boolean(token),
        refetchInterval: 15_000
    });
}

/** `around`: show the messages either side of this one (a search hit or pin) instead of the latest. */
export function useDirectChatMessages(id: string | null, around: string | null = null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["direct-chat-messages", id, token, around],
        queryFn: () =>
            id ? connectParityApi.listDirectChatMessages(id, token, around) : Promise.resolve([]),
        enabled: Boolean(id && token),
        refetchInterval: 5_000
    });
}

export function useSendDirectChatMessage(id: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (bodyHtml: string) => connectParityApi.sendDirectChatMessage(id, bodyHtml, token),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["direct-chat-messages", id] });
            void qc.invalidateQueries({ queryKey: ["direct-chats"] });
        }
    });
}

// --- DM / group-chat message actions ------------------------------------

/** Reaction chips for a DM ("dms"), group chat ("conversations") or meeting chat ("meetings"), polled like its messages. */
export function useChatReactions(scope: ChatScope, containerId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["chat-reactions", scope, containerId, token],
        queryFn: () =>
            containerId ? connectParityApi.listChatReactions(scope, containerId, token) : Promise.resolve([]),
        enabled: Boolean(containerId && token),
        refetchInterval: 5_000
    });
}

export function useChatMessageAction(scope: ChatScope, containerId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    const messagesKey = scope === "dms" ? "direct-chat-messages" : scope === "meetings" ? "meeting-chat" : "conversation-messages";
    return useMutation({
        mutationFn: (args:
            | { type: "edit"; messageId: string; bodyHtml: string }
            | { type: "delete"; messageId: string }
            | { type: "react" | "unreact"; messageId: string; emoji: string }) => {
            switch (args.type) {
                case "edit":
                    return connectParityApi.editChatMessage(scope, containerId, args.messageId, args.bodyHtml, token);
                case "delete":
                    return connectParityApi.deleteChatMessage(scope, containerId, args.messageId, token);
                case "react":
                    return connectParityApi.reactChatMessage(scope, containerId, args.messageId, args.emoji, token);
                case "unreact":
                    return connectParityApi.unreactChatMessage(scope, containerId, args.messageId, args.emoji, token);
            }
        },
        onSuccess: (_data, vars) => {
            if (vars.type === "react" || vars.type === "unreact") {
                void qc.invalidateQueries({ queryKey: ["chat-reactions", scope, containerId] });
            } else {
                void qc.invalidateQueries({ queryKey: [messagesKey, containerId] });
            }
        }
    });
}

/**
 * A meeting's stored chat. Realtime comes from the meetings hub (in the room)
 * or chat.updated (elsewhere), both of which invalidate ["meeting-chat", id];
 * the slow poll is only a safety net.
 */
export function useMeetingChatMessages(meetingId: string | null | undefined, around: string | null = null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["meeting-chat", meetingId, token, around],
        queryFn: () => connectParityApi.listMeetingMessages(meetingId!, token, around),
        enabled: Boolean(meetingId && token),
        refetchInterval: 30_000
    });
}

/** Organizer, invitees and people who joined — who "@" offers and who files are shared with. */
export function useMeetingChatMembers(meetingId: string | null | undefined) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["meeting-chat-members", meetingId, token],
        queryFn: () => connectParityApi.meetingChatMembers(meetingId!, token),
        enabled: Boolean(meetingId && token),
        staleTime: 60_000
    });
}

/**
 * The unified Chat list. Kept fresh by the hub's chat.updated / dm.message.sent
 * events (see ConnectShell); the slow poll is only a safety net.
 */
export function useChats() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["chats", token],
        queryFn: () => connectParityApi.listChats(token),
        enabled: Boolean(token),
        refetchInterval: 60_000,
        refetchOnWindowFocus: true
    });
}

export function useChannelMembers(teamId: string | null, channelId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["channel-members", teamId, channelId, token],
        queryFn: () =>
            teamId && channelId
                ? connectParityApi.listChannelMembers(teamId, channelId, token)
                : Promise.resolve([]),
        enabled: Boolean(teamId && channelId && token),
        staleTime: 30_000
    });
}

export function usePolls(channelId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["polls", channelId, token],
        queryFn: () => (channelId ? connectParityApi.listPolls(channelId, token) : Promise.resolve([])),
        enabled: Boolean(channelId && token)
    });
}

export function useCreatePoll(channelId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (p: { question: string; options: string[]; allowMultiple: boolean }) =>
            connectParityApi.createPoll(channelId, p, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["polls", channelId] })
    });
}

export function useVotePoll(channelId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (args: { pollId: string; optionIds: string[] }) =>
            connectParityApi.votePoll(channelId, args.pollId, args.optionIds, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["polls", channelId] })
    });
}

export function useMeetings() {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["meetings", token],
        queryFn: () => connectParityApi.listMeetings(token),
        enabled: Boolean(token)
    });
}

export function useCreateMeeting() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (p: { subject: string; scheduledStart: string; scheduledEnd?: string; channelId?: string; teamId?: string; invitees?: string[]; description?: string }) =>
            connectParityApi.createMeeting(p, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["meetings"] })
    });
}

export function useUpdateMeeting() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (p: { id: string; subject?: string; scheduledStart?: string; scheduledEnd?: string; description?: string }) =>
            connectParityApi.updateMeeting(p.id, { subject: p.subject, scheduledStart: p.scheduledStart, scheduledEnd: p.scheduledEnd, description: p.description }, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["meetings"] })
    });
}

export function useCancelMeeting() {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: (id: string) => connectParityApi.cancelMeeting(id, token),
        onSuccess: () => void qc.invalidateQueries({ queryKey: ["meetings"] })
    });
}

export function useUploadFile() {
    const token = useAccessToken();
    return useMutation({
        mutationFn: (file: File) => connectParityApi.uploadFile(file, token)
    });
}

export function useMessageReactions(channelId: string, messageId: string) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["reactions", channelId, messageId, token],
        queryFn: () => connectParityApi.listMessageReactions(channelId, messageId, token),
        enabled: Boolean(token),
        staleTime: 10_000
    });
}

// ---------- Channel posts, pins, polls ----------

/**
 * A channel's posts (top-level messages with their thread summaries), newest
 * page first; fetchNextPage loads older posts. With `around` set, the first
 * page is the posts around that post / reply (jump from search or a pin).
 * Kept fresh by the hub's channel events (see ConnectShell).
 */
export function useChannelPosts(channelId: string | null, around: string | null) {
    const token = useAccessToken();
    return useInfiniteQuery({
        queryKey: ["channel-posts", channelId, around, token],
        queryFn: ({ pageParam }) => connectPostsApi.listChannelPosts(
            channelId!,
            { before: pageParam, around: pageParam ? null : around, take: 30 },
            token),
        initialPageParam: null as string | null,
        getNextPageParam: (last) => (last.hasOlder && last.posts[0] ? last.posts[0].message.sentAt : undefined),
        enabled: Boolean(channelId && token)
    });
}

/** Unread counts for my channels in a team (the channel list's badges). */
export function useChannelUnread(teamId: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["channel-unread", teamId, token],
        queryFn: () => connectPostsApi.channelUnread(teamId!, token),
        enabled: Boolean(teamId && token),
        refetchInterval: 30_000
    });
}

/** Pinned messages of a channel, 1:1 chat or group chat. */
export function usePins(scope: PinScope, id: string | null) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["pins", scope, id, token],
        queryFn: () => connectPostsApi.listPins(scope, id!, token),
        enabled: Boolean(id && token),
        staleTime: 30_000
    });
}

/**
 * The last pin error in a conversation (e.g. the 25-pin limit), shown by its
 * PinnedBar whichever message's toolbar raised it. Client-only state kept in
 * the query cache so every component of that conversation sees it.
 */
export function usePinError(scope: PinScope, id: string | null) {
    const qc = useQueryClient();
    const key = ["pin-error", scope, id];
    const q = useQuery<string | null>({
        queryKey: key,
        queryFn: () => null,
        enabled: false,
        initialData: null,
        staleTime: Infinity
    });
    return { message: q.data, clear: () => qc.setQueryData(key, null) };
}

export function usePinToggle(scope: PinScope, id: string | null) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        mutationFn: ({ messageId, pin }: { messageId: string; pin: boolean }) =>
            connectPostsApi.setPin(scope, id!, messageId, pin, token),
        onMutate: () => qc.setQueryData(["pin-error", scope, id], null),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: ["pins", scope, id] });
            if (scope === "channels") void qc.invalidateQueries({ queryKey: ["channel-posts", id] });
        },
        onError: (e) => qc.setQueryData(
            ["pin-error", scope, id],
            e instanceof ConnectApiError && e.status === 403
                ? "Only team owners can pin messages in this channel."
                : e instanceof Error && e.message && !e.message.startsWith("Connect BFF")
                    ? e.message
                    : "Couldn't change the pin. Try again.")
    });
}

/** One poll's live results. poll.updated (hub) invalidates it; the slow poll is a safety net. */
export function usePoll(pollId: string) {
    const token = useAccessToken();
    return useQuery({
        queryKey: ["poll", pollId, token],
        queryFn: () => connectPostsApi.getPoll(pollId, token),
        enabled: Boolean(token),
        refetchInterval: 30_000,
        staleTime: 5_000,
        retry: (count, err) => !(err instanceof ConnectApiError && err.status === 404) && count < 2
    });
}

export function useMessageAction(channelId: string) {
    const qc = useQueryClient();
    const token = useAccessToken();
    return useMutation({
        onSuccess: (_data, vars) => {
            if (vars.type === "react" || vars.type === "unreact") {
                void qc.invalidateQueries({
                    queryKey: ["reactions", channelId, vars.messageId]
                });
            }
        },
        mutationFn: async (args:
            | { type: "delete"; messageId: string }
            | { type: "edit"; messageId: string; bodyHtml: string }
            | { type: "pin" | "unpin"; messageId: string }
            | { type: "react"; messageId: string; emoji: string }
            | { type: "unreact"; messageId: string; emoji: string }) => {
            switch (args.type) {
                case "delete":
                    return connectApi.deleteMessage(channelId, args.messageId, token);
                case "edit":
                    // The server broadcasts channel.message.edited, which the
                    // shell applies via cache.replaceMessage — no manual refetch.
                    return connectApi.editMessage(channelId, args.messageId, args.bodyHtml, token);
                case "pin":
                case "unpin":
                    return connectApi.pinMessage(channelId, args.messageId, args.type === "pin", token);
                case "react":
                    return connectApi.reactMessage(channelId, args.messageId, args.emoji, token);
                case "unreact": {
                    const baseUrl = import.meta.env.VITE_CONNECT_BFF_BASE_URL;
                    const res = await fetch(
                        `${baseUrl}/api/channels/${channelId}/messages/${args.messageId}/reactions/${encodeURIComponent(args.emoji)}`,
                        {
                            method: "DELETE",
                            headers: token ? { Authorization: `Bearer ${token}` } : {}
                        }
                    );
                    if (!res.ok) throw new Error(`unreact ${res.status}`);
                    return;
                }
            }
        }
    });
}
